import { notificationControllerFindAll, notificationControllerRetry } from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Bell,
  BookOpen,
  CheckCircle,
  Clock,
  CreditCard,
  Eye,
  FileCheck,
  Lock,
  RefreshCw,
  Search,
  Send,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { StatCard } from '../../components/shared/stat-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NotifItem {
  id: string;
  type: string;
  recipientEmail: string;
  recipientName?: string | null;
  subject: string;
  status: 'QUEUED' | 'SENT' | 'FAILED';
  sentAt?: string | null;
  createdAt: string;
  retryCount: number;
  failureReason?: string | null;
  request?: { id: string; referenceNo: string } | null;
}

interface PagedResponse {
  items: NotifItem[];
  total: number;
  page: number;
  limit: number;
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/notifications/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('notification:view')) throw redirect({ to: '/dashboard' });
  },
  component: NotificationsPage,
});

// ── Config maps ───────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  PAYMENT_REMINDER: 'Payment Reminder',
  INSPECTION_REQUIRED: 'Inspection Required',
  INSPECTOR_ASSIGNED: 'Inspector Assigned',
  APPROVAL_REQUESTED: 'Approval Requested',
  PERMIT_APPROVED: 'Permit Approved',
  PERMIT_REJECTED: 'Permit Rejected',
  PAYMENT_RECEIVED: 'Payment Received',
  PERMIT_ISSUED: 'Permit Issued',
  PLEDGE_STUDY_REQUIRED: 'Pledge Study Required',
  PLEDGE_STUDY_APPROVAL_REQUESTED: 'Pledge Study Approval',
  PASSWORD_RESET_REQUESTED: 'Password Reset',
  CUSTOMER_ACCOUNT_CREATED: 'Account Created',
  PAYMENT_LINK_SENT: 'Payment Link Sent',
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  PAYMENT_REMINDER: <CreditCard size={16} />,
  PAYMENT_RECEIVED: <CreditCard size={16} />,
  INSPECTION_REQUIRED: <Eye size={16} />,
  INSPECTOR_ASSIGNED: <Eye size={16} />,
  APPROVAL_REQUESTED: <FileCheck size={16} />,
  PERMIT_APPROVED: <FileCheck size={16} />,
  PERMIT_REJECTED: <FileCheck size={16} />,
  PERMIT_ISSUED: <FileCheck size={16} />,
  PLEDGE_STUDY_REQUIRED: <BookOpen size={16} />,
  PLEDGE_STUDY_APPROVAL_REQUESTED: <BookOpen size={16} />,
  PASSWORD_RESET_REQUESTED: <Lock size={16} />,
  CUSTOMER_ACCOUNT_CREATED: <UserPlus size={16} />,
  PAYMENT_LINK_SENT: <Send size={16} />,
};

const TYPE_AVATAR_COLOR: Record<string, string> = {
  PAYMENT_REMINDER: 'from-amber-500 to-orange-600',
  PAYMENT_RECEIVED: 'from-emerald-500 to-green-600',
  INSPECTION_REQUIRED: 'from-orange-500 to-red-600',
  INSPECTOR_ASSIGNED: 'from-sky-500 to-cyan-600',
  APPROVAL_REQUESTED: 'from-violet-500 to-purple-600',
  PERMIT_APPROVED: 'from-emerald-500 to-teal-600',
  PERMIT_REJECTED: 'from-red-500 to-rose-600',
  PERMIT_ISSUED: 'from-teal-500 to-cyan-600',
  PLEDGE_STUDY_REQUIRED: 'from-indigo-500 to-violet-600',
  PLEDGE_STUDY_APPROVAL_REQUESTED: 'from-purple-500 to-indigo-600',
  PASSWORD_RESET_REQUESTED: 'from-gray-500 to-slate-600',
  CUSTOMER_ACCOUNT_CREATED: 'from-blue-500 to-indigo-600',
  PAYMENT_LINK_SENT: 'from-emerald-500 to-teal-600',
};

const TYPE_OPTIONS = [
  { value: 'ALL', label: 'All types' },
  { value: 'PAYMENT_REMINDER', label: 'Payment Reminder' },
  { value: 'INSPECTION_REQUIRED', label: 'Inspection Required' },
  { value: 'INSPECTOR_ASSIGNED', label: 'Inspector Assigned' },
  { value: 'APPROVAL_REQUESTED', label: 'Approval Requested' },
  { value: 'PERMIT_APPROVED', label: 'Permit Approved' },
  { value: 'PERMIT_REJECTED', label: 'Permit Rejected' },
  { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
  { value: 'PERMIT_ISSUED', label: 'Permit Issued' },
  { value: 'PLEDGE_STUDY_REQUIRED', label: 'Pledge Study Required' },
  { value: 'PLEDGE_STUDY_APPROVAL_REQUESTED', label: 'Pledge Study Approval' },
  { value: 'PASSWORD_RESET_REQUESTED', label: 'Password Reset' },
  { value: 'CUSTOMER_ACCOUNT_CREATED', label: 'Account Created' },
  { value: 'PAYMENT_LINK_SENT', label: 'Payment Link Sent' },
];

type StatusFilter = 'all' | 'QUEUED' | 'SENT' | 'FAILED';

// ── Helpers ───────────────────────────────────────────────────────────────────

function NotifAvatar({ type }: { type: string }) {
  const gradient = TYPE_AVATAR_COLOR[type] ?? 'from-gray-400 to-gray-500';
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
        gradient,
      )}
    >
      {TYPE_ICON[type] ?? <Bell size={16} />}
    </div>
  );
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Columns ───────────────────────────────────────────────────────────────────

const NOTIF_COLUMNS: CardDataTableColumn[] = [
  { id: 'notification', header: 'Notification' },
  { id: 'recipient', header: 'Recipient' },
  { id: 'permit', header: 'Permit', align: 'center' },
  { id: 'time', header: 'Time' },
  { id: 'status', header: 'Status', align: 'center' },
];

const NOTIF_GRID = 'minmax(0,1.5fr) minmax(140px, 1fr) 130px 150px 100px';

// ── Page ──────────────────────────────────────────────────────────────────────

function NotificationsPage() {
  const { can } = usePermissions();
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [type, setType] = useState('ALL');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 20;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, type, debounced]);

  const queryKey = ['notifications', statusFilter, type, debounced, page];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      notificationControllerFindAll({
        ...(statusFilter !== 'all' ? { status: statusFilter as 'QUEUED' | 'SENT' | 'FAILED' } : {}),
        ...(type !== 'ALL'
          ? {
              type: type as
                | 'INSPECTION_REQUIRED'
                | 'INSPECTOR_ASSIGNED'
                | 'APPROVAL_REQUESTED'
                | 'PERMIT_APPROVED'
                | 'PERMIT_REJECTED'
                | 'PAYMENT_RECEIVED'
                | 'PERMIT_ISSUED'
                | 'PLEDGE_STUDY_REQUIRED'
                | 'PLEDGE_STUDY_APPROVAL_REQUESTED'
                | 'PAYMENT_REMINDER'
                | 'PASSWORD_RESET_REQUESTED'
                | 'CUSTOMER_ACCOUNT_CREATED'
                | 'PAYMENT_LINK_SENT',
            }
          : {}),
        page,
        limit: LIMIT,
      }) as unknown as Promise<PagedResponse>,
  });

  // Stat counts (independent of filters — all-time system health)
  const { data: sentStat } = useQuery({
    queryKey: ['notif-stat', 'SENT'],
    queryFn: () =>
      notificationControllerFindAll({
        status: 'SENT',
        page: 1,
        limit: 1,
      }) as unknown as Promise<PagedResponse>,
    staleTime: 30_000,
  });
  const { data: failedStat } = useQuery({
    queryKey: ['notif-stat', 'FAILED'],
    queryFn: () =>
      notificationControllerFindAll({
        status: 'FAILED',
        page: 1,
        limit: 1,
      }) as unknown as Promise<PagedResponse>,
    staleTime: 30_000,
  });
  const { data: queuedStat } = useQuery({
    queryKey: ['notif-stat', 'QUEUED'],
    queryFn: () =>
      notificationControllerFindAll({
        status: 'QUEUED',
        page: 1,
        limit: 1,
      }) as unknown as Promise<PagedResponse>,
    staleTime: 30_000,
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => notificationControllerRetry({ id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notif-stat'] });
    },
  });

  const items = data?.items ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / LIMIT);

  const totalSent = sentStat?.total ?? 0;
  const totalFailed = failedStat?.total ?? 0;
  const totalQueued = queuedStat?.total ?? 0;
  const totalAll = totalSent + totalFailed + totalQueued;

  return (
    <AppLayout>
      <PageHeader
        title="Notification Logs"
        description="System notification history and delivery status."
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Bell size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          delta="All time"
          value={totalAll}
          label="Total Sent"
          loading={!sentStat && !failedStat && !queuedStat}
        />
        <StatCard
          icon={<CheckCircle size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta="Delivered"
          value={totalSent}
          label="Sent"
          loading={!sentStat}
        />
        <StatCard
          icon={<AlertCircle size={18} />}
          iconBg="bg-red-50"
          iconColor="text-red-500"
          delta="Need attention"
          value={totalFailed}
          label="Failed"
          loading={!failedStat}
        />
        <StatCard
          icon={<Clock size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          delta="Awaiting delivery"
          value={totalQueued}
          label="Queued"
          loading={!queuedStat}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        {/* Search */}
        <div className="relative min-w-52">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permit reference…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(['all', 'QUEUED', 'SENT', 'FAILED'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setStatusFilter(v)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                statusFilter === v
                  ? v === 'FAILED'
                    ? 'bg-red-500 text-white shadow-sm'
                    : v === 'SENT'
                      ? 'bg-success-500 text-white shadow-sm'
                      : v === 'QUEUED'
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {v === 'all' ? 'All' : v.charAt(0) + v.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {/* Type dropdown */}
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-40 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto text-xs text-gray-400">{data?.total ?? 0} results</span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={NOTIF_GRID}
        columns={NOTIF_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load notifications</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <Bell size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No notifications found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your filters</p>
          </div>
        }
        rows={items.map(
          (n): CardDataTableRowDef => ({
            id: n.id,
            avatar: <NotifAvatar type={n.type} />,
            cells: {
              notification: (
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className={cn(
                        'shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                        'border-gray-200 bg-gray-50 text-gray-600',
                      )}
                    >
                      {TYPE_LABEL[n.type] ?? n.type.replace(/_/g, ' ')}
                    </span>
                    {n.retryCount > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                        <RefreshCw size={9} />
                        {n.retryCount}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-sm font-medium text-gray-800 leading-snug">
                    {n.subject}
                  </p>
                  {n.status === 'FAILED' && n.failureReason && (
                    <p className="mt-0.5 truncate text-xs text-red-500">{n.failureReason}</p>
                  )}
                </div>
              ),
              recipient: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800">{n.recipientEmail}</p>
                  {n.recipientName && (
                    <p className="truncate text-xs text-gray-400">{n.recipientName}</p>
                  )}
                </div>
              ),
              permit: n.request?.referenceNo ? (
                <span className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-600">
                  {n.request.referenceNo}
                </span>
              ) : (
                <span className="text-xs text-gray-300">—</span>
              ),
              time: (
                <div className="min-w-0">
                  <p className="text-xs text-gray-600">{formatTime(n.sentAt ?? n.createdAt)}</p>
                  {n.sentAt && n.sentAt !== n.createdAt && (
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Created {formatTime(n.createdAt)}
                    </p>
                  )}
                </div>
              ),
              status: (
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
                    n.status === 'SENT'
                      ? 'bg-success-50 border border-success-200 text-success-700'
                      : n.status === 'FAILED'
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-amber-50 border border-amber-200 text-amber-700',
                  )}
                >
                  {n.status === 'SENT' && <CheckCircle size={10} />}
                  {n.status === 'FAILED' && <AlertCircle size={10} />}
                  {n.status === 'QUEUED' && <Clock size={10} />}
                  {n.status.charAt(0) + n.status.slice(1).toLowerCase()}
                </span>
              ),
            },
            ...(n.status === 'FAILED' && can('notification:view')
              ? {
                  actions: [
                    {
                      icon: <RefreshCw size={13} />,
                      label: 'Retry',
                      onClick: (e) => {
                        e.stopPropagation();
                        retryMutation.mutate(n.id);
                      },
                    },
                  ],
                }
              : {}),
            expandContent: n.failureReason ? (
              <div className="flex items-start gap-2">
                <AlertCircle size={12} className="shrink-0 text-red-500 mt-0.5" />
                <p className="text-xs text-red-600 font-mono truncate">{n.failureReason}</p>
              </div>
            ) : undefined,
          }),
        )}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {items.length} of {data?.total ?? 0} notifications
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
